import { z } from "zod";

/**
 * Per-list campaign routing (PREC-4219): an ordered set of rules, each "these conditions → that
 * campaign", evaluated top to bottom for a lead that has no explicit campaign choice. The first
 * matching rule whose campaign can take leads wins; with no match the list's fallback decides.
 * Routing only *preassigns* (it writes the lead's campaign, never its status): enrollment is the
 * auto-assign toggle's or the user's decision.
 *
 * The API evaluates the rules (`campaign-routing.util.ts`); the web edits them. Both read the
 * contract below, so a field or operator one side knows and the other does not is a type error.
 */

/** The lead facts a condition can test, in the order the rule editor offers them. */
export const CAMPAIGN_ROUTING_FIELDS = [
	"PERSONAL_COUNTRY",
	"COMPANY_COUNTRY",
	"PERSONA_SEGMENT",
	"COMPANY_SEGMENT",
	"PRIORITY",
	"SENIORITY",
	"INDUSTRY",
	"JOB_TITLE",
	"NUMBER_OF_EMPLOYEES",
	"PROFILE_LANGUAGE",
] as const;

export type CampaignRoutingField = (typeof CAMPAIGN_ROUTING_FIELDS)[number];

export const CAMPAIGN_ROUTING_OPERATORS = [
	"IS_ANY_OF",
	"IS_NONE_OF",
	"IS_EMPTY",
	"IS_NOT_EMPTY",
	"CONTAINS",
	"DOES_NOT_CONTAIN",
	"BETWEEN",
] as const;

export type CampaignRoutingOperator = (typeof CAMPAIGN_ROUTING_OPERATORS)[number];

const PICK_OPERATORS: readonly CampaignRoutingOperator[] = [
	"IS_ANY_OF",
	"IS_NONE_OF",
	"IS_EMPTY",
	"IS_NOT_EMPTY",
];

/** Which operators each field takes: Job title is text, Number of employees a range. */
export const CAMPAIGN_ROUTING_FIELD_OPERATORS: Record<
	CampaignRoutingField,
	readonly CampaignRoutingOperator[]
> = {
	PERSONAL_COUNTRY: PICK_OPERATORS,
	COMPANY_COUNTRY: PICK_OPERATORS,
	PERSONA_SEGMENT: PICK_OPERATORS,
	COMPANY_SEGMENT: PICK_OPERATORS,
	PRIORITY: PICK_OPERATORS,
	SENIORITY: PICK_OPERATORS,
	INDUSTRY: PICK_OPERATORS,
	JOB_TITLE: ["CONTAINS", "DOES_NOT_CONTAIN", "IS_EMPTY", "IS_NOT_EMPTY"],
	NUMBER_OF_EMPLOYEES: ["BETWEEN", "IS_EMPTY", "IS_NOT_EMPTY"],
	PROFILE_LANGUAGE: PICK_OPERATORS,
};

/**
 * The fields read from this company's own lead scoring. A lead the company has not scored matches
 * no condition on them, whatever the operator: "is empty" on a segment means "scored, without a
 * segment", never "not scored yet".
 */
export const CAMPAIGN_ROUTING_SCORING_FIELDS: readonly CampaignRoutingField[] = [
	"PERSONA_SEGMENT",
	"COMPANY_SEGMENT",
	"PRIORITY",
];

/** The operators that compare against `values` (and need at least one). */
export const CAMPAIGN_ROUTING_VALUE_OPERATORS: readonly CampaignRoutingOperator[] = [
	"IS_ANY_OF",
	"IS_NONE_OF",
	"CONTAINS",
	"DOES_NOT_CONTAIN",
];

/** What a list does with a lead no rule matched: its "(Default)" campaign, or none. */
export const CAMPAIGN_ROUTING_FALLBACKS = ["DEFAULT_CAMPAIGN", "NO_CAMPAIGN"] as const;

export type CampaignRoutingFallback = (typeof CAMPAIGN_ROUTING_FALLBACKS)[number];

/**
 * Why a lead holds its campaign (`list_person.campaignAssignedBy`): a routing rule, the list's
 * default fallback, or a person (or caller) who named it. NULL on a row with a campaign is a choice
 * made before routing existed, and is read as a person's.
 */
export const CAMPAIGN_ASSIGNED_BY = ["RULE", "DEFAULT", "USER"] as const;

export type CampaignAssignedBy = (typeof CAMPAIGN_ASSIGNED_BY)[number];

export const CAMPAIGN_ROUTING_MAX_RULES = 50;
export const CAMPAIGN_ROUTING_MAX_CONDITIONS = 20;
export const CAMPAIGN_ROUTING_MAX_VALUES = 200;

export const isCampaignRoutingOperatorAllowed = (
	field: CampaignRoutingField,
	operator: CampaignRoutingOperator
): boolean => CAMPAIGN_ROUTING_FIELD_OPERATORS[field].includes(operator);

const employeeBound = z.number().int().min(0).nullable().optional();

/**
 * One condition: `values` for the pick and text operators (OR across them), `from`/`to` for
 * Number of employees. A condition the evaluator could not read is refused here, on save.
 */
export const campaignRoutingConditionSchema = z
	.object({
		field: z.enum(CAMPAIGN_ROUTING_FIELDS),
		operator: z.enum(CAMPAIGN_ROUTING_OPERATORS),
		values: z
			.array(z.string().trim().min(1).max(255))
			.max(CAMPAIGN_ROUTING_MAX_VALUES)
			.optional(),
		from: employeeBound,
		to: employeeBound,
	})
	.superRefine((condition, context) => {
		if (!isCampaignRoutingOperatorAllowed(condition.field, condition.operator)) {
			context.addIssue({
				code: "custom",
				message: `${condition.field} does not take ${condition.operator}`,
				path: ["operator"],
			});
		}

		const needsValues = CAMPAIGN_ROUTING_VALUE_OPERATORS.includes(condition.operator);

		if (needsValues && !condition.values?.length) {
			context.addIssue({
				code: "custom",
				message: `${condition.operator} needs at least one value`,
				path: ["values"],
			});
		}

		if (condition.operator !== "BETWEEN") {
			return;
		}

		const from = condition.from ?? null;
		const to = condition.to ?? null;

		if (from === null && to === null) {
			context.addIssue({ code: "custom", message: "BETWEEN needs from or to", path: ["from"] });
		} else if (from !== null && to !== null && from > to) {
			context.addIssue({ code: "custom", message: "from is above to", path: ["to"] });
		}
	});

export type CampaignRoutingCondition = z.infer<typeof campaignRoutingConditionSchema>;

/** One rule as the editor saves it: its conditions (AND) and the campaign of this list it routes to. */
export const campaignRoutingRuleInputSchema = z.object({
	conditions: z.array(campaignRoutingConditionSchema).min(1).max(CAMPAIGN_ROUTING_MAX_CONDITIONS),
	campaignUuid: z.string().trim().min(1).max(64),
});

export type CampaignRoutingRuleInput = z.infer<typeof campaignRoutingRuleInputSchema>;

/** `PUT list/:id/campaign-routing`: the whole ordered set and the fallback, replaced at once. */
export const campaignRoutingSaveSchema = z.object({
	fallback: z.enum(CAMPAIGN_ROUTING_FALLBACKS),
	rules: z.array(campaignRoutingRuleInputSchema).max(CAMPAIGN_ROUTING_MAX_RULES),
});

export type CampaignRoutingSave = z.infer<typeof campaignRoutingSaveSchema>;

/**
 * A rule's target as the editor warns about it. VALID, PAUSED, NOT_ACTIVATED and ENDED campaigns
 * take preassigned leads (sending still waits for the campaign to run); INVALID and DELETED ones
 * are skipped, and the next rule is tried.
 */
export const CAMPAIGN_ROUTING_TARGET_STATES = [
	"VALID",
	"PAUSED",
	"NOT_ACTIVATED",
	"ENDED",
	"INVALID",
	"DELETED",
] as const;

export type CampaignRoutingTargetState = (typeof CAMPAIGN_ROUTING_TARGET_STATES)[number];

/** Whether routing may preassign a lead to a campaign in this state. */
export const isCampaignRoutingTargetUsable = (state: CampaignRoutingTargetState): boolean =>
	state !== "INVALID" && state !== "DELETED";

export const campaignRoutingRuleSchema = z.object({
	id: z.number(),
	position: z.number(),
	conditions: z.array(campaignRoutingConditionSchema),
	campaignUuid: z.string(),
	targetState: z.enum(CAMPAIGN_ROUTING_TARGET_STATES),
});

export type CampaignRoutingRule = z.infer<typeof campaignRoutingRuleSchema>;

/** `GET` / `PUT list/:id/campaign-routing`: the rules in order, the fallback and the default it uses. */
export const campaignRoutingSettingsSchema = z.object({
	listId: z.number(),
	fallback: z.enum(CAMPAIGN_ROUTING_FALLBACKS),
	defaultCampaignUuid: z.string().nullable(),
	defaultCampaignState: z.enum(CAMPAIGN_ROUTING_TARGET_STATES).nullable(),
	rules: z.array(campaignRoutingRuleSchema),
});

export type CampaignRoutingSettings = z.infer<typeof campaignRoutingSettingsSchema>;

/**
 * Why "Confirm preassigned" left a selected lead where it was. NOT_INCLUDED: the lead is not among
 * the list's leads (blacklisted, unsure, not found or skipped), and neither bulk action touches it.
 */
export const CAMPAIGN_CONFIRM_SKIP_REASONS = [
	"NO_CAMPAIGN",
	"NOT_INCLUDED",
	"ALREADY_ENROLLED",
	"CAMPAIGN_NOT_USABLE",
	"PLACEHOLDER",
	"FAILED",
] as const;

export type CampaignConfirmSkipReason = (typeof CAMPAIGN_CONFIRM_SKIP_REASONS)[number];

/** Why "Preassign to campaign" left a selected lead's campaign as it was. */
export const CAMPAIGN_PREASSIGN_SKIP_REASONS = [
	"NOT_INCLUDED",
	"ALREADY_ENROLLED",
	"PLACEHOLDER",
] as const;

export type CampaignPreassignSkipReason = (typeof CAMPAIGN_PREASSIGN_SKIP_REASONS)[number];

export const campaignConfirmResultSchema = z.object({
	confirmed: z.coerce.number(),
	skipped: z.array(
		z.object({ reason: z.enum(CAMPAIGN_CONFIRM_SKIP_REASONS), count: z.coerce.number() })
	),
});

export type CampaignConfirmResult = z.infer<typeof campaignConfirmResultSchema>;

export const campaignPreassignResultSchema = z.object({
	preassigned: z.coerce.number(),
	skipped: z.array(
		z.object({ reason: z.enum(CAMPAIGN_PREASSIGN_SKIP_REASONS), count: z.coerce.number() })
	),
});

export type CampaignPreassignResult = z.infer<typeof campaignPreassignResultSchema>;
